import { useState } from 'react';
import hash from 'object-hash';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import style from '@core/Page/LeftMenu/LeftMenu.module.scss';
import itemStyle from '@core/Page/LeftMenu/NavigationItem/NavigationItem.module.scss';

import { NavigationItemParams } from '@store/App/LeftMenu';
import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { FixedIcon, Icon, IconType } from '@components/Icon';
import { ScrollBar } from '@components/ScrollBar';

import { NavigationItem } from '@core/Page/LeftMenu/NavigationItem';
import { UserProfile } from '@core/Page/LeftMenu/UserProfile';
import { ChangeLanguageButton } from '@components/ActionButtons/ChangeLanguageButton';
import { translate } from '@chaika/i18n/helper';
import { AppPortal } from '@components/AppPortal';
import { SupportWidget } from '@components/SupportWidget';
import { useSupportInbox } from '@components/SupportWidget/hooks/useSupportInbox';
import { LeftMenuAttributes } from 'chaika-test-attributes';

import { useLeftMenu } from './hooks/useLeftMenu';

export interface LeftMenuParams {
    additionalContent?: React.ComponentType;
}

export const LeftMenu = observer(({ additionalContent: AdditionalContent }: LeftMenuParams) => {
    const {
        navigationItems,
        isOpen,
        isTable,
        isOpenAndTable,
        withPhoneWidget,
        withSupportWidget,
        isPhoneWidgetOpen,
        isMobile,
        disableOpen,
        toggleIsLeftMenuOpen,
        closeMenu,
        onLinkClick,
        onSubItemTitleClick,
        onLogoutClick,
        onTogglePhoneWidgetToggle,
    } = useLeftMenu();

    return (
        <ClickOutsideAlerter onClickOutside={closeMenu}>
            <div className={classNames([style.root, isOpen && style.open])}>
                <div className={style.leftMenu}>
                    {isOpenAndTable && (
                        <div
                            className={style.closeIcon}
                            onClick={toggleIsLeftMenuOpen}
                        >
                            <Icon type={IconType.CROSS} />
                        </div>
                    )}
                    <div className={style.logoIcon}>
                        <div className={style.logoWrapper}>
                            {isOpen ? <Icon type={IconType.LOGO} /> : FixedIcon.getLogoShort()}
                        </div>
                    </div>
                    <div className={style.changeLanguageButton}>
                        <ChangeLanguageButton />
                    </div>
                    {isTable && AdditionalContent && (
                        <div
                            className={style.additionalContentWrapper}
                            onClick={onLinkClick}
                        >
                            <AdditionalContent />
                        </div>
                    )}
                    <ScrollBar
                        className={style.navigationMenuWrapper}
                        maxHeight={'calc(100% - 230px)'}
                        hide
                    >
                        <div className={style.navigationMenu}>
                            {navigationItems.map(item =>
                                item.subItems ? (
                                    <WithSubItems
                                        isOpen={isOpen}
                                        key={hash(item)}
                                        item={item}
                                        subItems={item.subItems}
                                        onSubItemTitleClick={onSubItemTitleClick}
                                        onLinkClick={onLinkClick}
                                    />
                                ) : (
                                    <NavigationItem
                                        key={hash(item)}
                                        title={translate(item.title)}
                                        dataTest={item.dataTest}
                                        link={item.link}
                                        blank={item.blank}
                                        iconType={item.iconType}
                                        onLinkClick={onLinkClick}
                                    />
                                ),
                            )}
                            {withPhoneWidget && (
                                <div
                                    className={classNames([
                                        itemStyle.root,
                                        style.widgetToggle,
                                        isPhoneWidgetOpen && itemStyle.active,
                                    ])}
                                    onClick={onTogglePhoneWidgetToggle}
                                >
                                    <div className={classNames([itemStyle.icon])}>
                                        <Icon type={IconType.PHONE} />
                                    </div>
                                    <div className={itemStyle.title}>{translate('phone')}</div>
                                </div>
                            )}
                            {withSupportWidget && (
                                <SupportMenuItem
                                    isMobile={isMobile}
                                    isLeftMenuOpen={isOpen}
                                />
                            )}
                        </div>
                    </ScrollBar>
                    {!disableOpen && (
                        <div className={style.openMenuButtonWrapper}>
                            <div
                                className={style.openMenuButton}
                                onClick={toggleIsLeftMenuOpen}
                            >
                                <div className={style.openMenuIcon}>
                                    <Icon type={IconType.ARROW_DOWN} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={style.profileLink}>
                        <UserProfile isOpen={isOpen} />
                    </div>
                    <div
                        className={style.logoutButton}
                        onClick={onLogoutClick}
                    >
                        {translate('exit')}
                    </div>
                </div>
            </div>
        </ClickOutsideAlerter>
    );
});

function WithSubItems({
    item,
    isOpen,
    onLinkClick,
    subItems,
    onSubItemTitleClick,
}: {
    isOpen: boolean;
    item: NavigationItemParams;
    subItems: NavigationItemParams[];
    onLinkClick(): void;
    onSubItemTitleClick(): void;
}) {
    return (
        <div className={style.navigationMenu}>
            <div
                className={style.item}
                onClick={onSubItemTitleClick}
                data-test={item.dataTest}
                data-testid={item.dataTest}
            >
                <div className={itemStyle.icon}>{item.iconType && <Icon type={item.iconType} />}</div>
                <div className={itemStyle.title}>{item.title}</div>
            </div>
            <div className={style.subItems}>
                {isOpen &&
                    subItems.map(item => (
                        <NavigationItem
                            isSubItem
                            dataTest={item.dataTest}
                            key={hash(item)}
                            title={translate(item.title)}
                            link={item.link}
                            iconType={item.iconType}
                            onLinkClick={onLinkClick}
                        />
                    ))}
            </div>
        </div>
    );
}

interface SupportMenuItemProps {
    isMobile: boolean;
    isLeftMenuOpen: boolean;
}

function SupportMenuItem({ isMobile, isLeftMenuOpen }: SupportMenuItemProps) {
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const inbox = useSupportInbox();

    const handleToggle = (): void => {
        if (isWidgetOpen) {
            inbox.closeTicket();
        } else {
            inbox.resetFilter();
        }
        setIsWidgetOpen(isOpen => !isOpen);
    };

    const handleClose = (): void => {
        inbox.closeTicket();
        setIsWidgetOpen(false);
    };

    return (
        <>
            <div
                className={classNames([itemStyle.root, style.widgetToggle, isWidgetOpen && itemStyle.active])}
                data-test={LeftMenuAttributes.Support}
                data-testid={LeftMenuAttributes.Support}
                onClick={handleToggle}
            >
                <div className={classNames(itemStyle.icon, style.supportIcon)}>
                    <Icon type={IconType.SUPPORT} />
                    {inbox.totalUnread > 0 && (
                        <span
                            className={style.supportUnreadBadge}
                            data-test={'leftMenu_supportUnreadBadge'}
                            data-testid={'leftMenu_supportUnreadBadge'}
                            aria-label={translate('supportUnreadCount', { $count: String(inbox.totalUnread) })}
                        >
                            {inbox.totalUnread > 99 ? '99+' : inbox.totalUnread}
                        </span>
                    )}
                </div>
                <div className={itemStyle.title}>{translate('supportTitle')}</div>
            </div>
            {isWidgetOpen && (
                <AppPortal>
                    <SupportWidget
                        inbox={inbox}
                        isMobile={isMobile}
                        isLeftMenuOpen={isLeftMenuOpen}
                        onClose={handleClose}
                    />
                </AppPortal>
            )}
        </>
    );
}
